import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services/user.service';

/** Riga del modale: etichetta leggibile, valore formattato e unità. */
interface AllDataRow {
  label: string;
  value: string;
  unit: string;
}

/**
 * Modale "Tutti i dati" del simulatore NERF: tutti i valori restituiti dalla Lambda
 * per una simulazione, in tre schede (metriche operative, costi, parametri di input)
 * con etichetta, valore e unità nella lingua dell'utente loggato.
 */
@Component({
  selector: 'app-modal-nerf-all-data',
  templateUrl: './modal-nerf-all-data.component.html',
  standalone: false
})
export class ModalNerfAllDataComponent {
  public readonly columns: string[] = ['label', 'value', 'unit'];
  public readonly groups: { titleKey: string; rows: AllDataRow[] }[];
  /** Locale di numeri e data, secondo la lingua dell'utente ('it-IT' / 'en-US', come nel resto dell'app). */
  public readonly locale: string;

  /** Lingua dell'utente loggato, senza il suffisso dell'organizzazione (it-simulator -> it). */
  private readonly language: 'it' | 'en';

  /** Risultati operativi e di capacità calcolati dalla Lambda; i costi calcolati si riconoscono dal nome. */
  private readonly operationalResultKeys: ReadonlyArray<string> = [
    'patients_per_month', 'avg_infusion_time_mix', 'post_infusion_monitoring_time_weighted',
    'cycles_per_chair_day', 'effective_chairs', 'nurse_capacity', 'gap_total',
    'rotation_index', 'utilization_percent', 'infusion_ratio',
    'patients_per_infusion', 'infusions_per_year', 'patients_in_care', 'chair_cycle_min',
  ];

  /** Chiavi dei risultati la cui etichetta nei file di lingua ha un nome diverso. */
  private readonly labelAliases: Record<string, string> = {
    ocrelizumab_subcutaneous_min: 'rituximab_min_subcutaneous',
    ocrelizumab_intravenous_min: 'ocrelizumab_min_intravenous',
    ocrelizumab_subcutaneous_composition: 'rituximab_composition',
    ocrelizumab_intravenous_composition: 'intravenous_ocrelizumab_composition',
    ocrelizumab_intravenous_cost_single_infusion: 'intravenous_ocrelizumab_cost_single_infusion',
    ocrelizumab_subcutaneous_cost_single_infusion: 'subcutaneous_ocrelizumab_cost_single_infusion',
    cost_for_month: 'total_monthly_cost',
    cost_for_patient: 'cost_per_patient',
    cost_for_year: 'total_yearly_cost',
    avg_cost_mix: 'average_cost_mix',
    room_hourly_cost_for_month: 'costo_orario_sala_per_month',
    chair_hourly_cost_for_month: 'costo_per_ora_per_poltrona',
  };

  /**
   * Etichette dei valori che non hanno ancora una traduzione nei file di lingua:
   * usate solo se il file di lingua non ha la chiave.
   */
  private readonly fallbackLabels: Record<'it' | 'en', Record<string, string>> = {
    it: {
      bureaucracy_time: 'Tempo pratiche amministrative',
      ocrelizumab_subcutaneous_administrations_per_year: 'Somministrazioni annue ocrelizumab sottocute',
      ocrelizumab_intravenous_administrations_per_year: 'Somministrazioni annue ocrelizumab endovena',
      ublituximab_administrations_per_year: 'Somministrazioni annue ublituximab',
      natalizumab_intravenous_administrations_per_year: 'Somministrazioni annue natalizumab endovena',
      natalizumab_subcutaneous_administrations_per_year: 'Somministrazioni annue natalizumab sottocute',
      patients_per_infusion: 'Pazienti per infusione',
      post_infusion_monitoring_time_weighted: 'Tempo di monitoraggio post-infusione (media ponderata)',
      gap_total: 'Tempo di attesa totale',
      infusions_per_year: 'Infusioni annue',
      patients_in_care: 'Pazienti in carico',
      chair_cycle_min: 'Durata ciclo postazione',
      infusion_ratio: 'Rapporto infusioni',
      materials_cost_for_month: 'Costo materiali mensile',
      avg_cost_mix_for_month: 'Costo medio mix mensile',
      nurses_total_cost_for_month: 'Costo totale infermieri mensile',
      cost_for_infusion: 'Costo per infusione',
      cost_for_year: 'Costo totale annuo',
      cost_per_patient_year: 'Costo annuo per paziente',
      drug_cost_per_patient_year: 'Costo farmaco annuo per paziente',
    },
    en: {
      bureaucracy_time: 'Paperwork time',
      ocrelizumab_subcutaneous_administrations_per_year: 'Yearly administrations of subcutaneous ocrelizumab',
      ocrelizumab_intravenous_administrations_per_year: 'Yearly administrations of intravenous ocrelizumab',
      ublituximab_administrations_per_year: 'Yearly administrations of ublituximab',
      natalizumab_intravenous_administrations_per_year: 'Yearly administrations of intravenous natalizumab',
      natalizumab_subcutaneous_administrations_per_year: 'Yearly administrations of subcutaneous natalizumab',
      patients_per_infusion: 'Patients per infusion',
      post_infusion_monitoring_time_weighted: 'Post-infusion monitoring time (weighted average)',
      gap_total: 'Total waiting time',
      infusions_per_year: 'Infusions per year',
      patients_in_care: 'Patients in care',
      chair_cycle_min: 'Chair cycle duration',
      infusion_ratio: 'Infusion ratio',
      materials_cost_for_month: 'Monthly materials cost',
      avg_cost_mix_for_month: 'Monthly average mix cost',
      nurses_total_cost_for_month: 'Monthly total nursing cost',
      cost_for_infusion: 'Cost per infusion',
      cost_for_year: 'Total yearly cost',
      cost_per_patient_year: 'Yearly cost per patient',
      drug_cost_per_patient_year: 'Yearly drug cost per patient',
    },
  };

  /** Unità che cambiano con la lingua. */
  private readonly dayUnit: Record<'it' | 'en', string> = { it: 'giorni', en: 'days' };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { result: any; createdAt?: string | null },
    private translateService: TranslateService,
    private userService: UserService
  ) {
    // Lingua impostata per l'utente loggato (userDetails.language); in mancanza quella attiva
    // di ngx-translate, che resetCurrentLanguage() ricava comunque da userDetails.language.
    const userLanguage = (this.userService.userDetails?.language || this.translateService.currentLang || 'it')
      .toString().toLowerCase();
    this.language = userLanguage.startsWith('en') ? 'en' : 'it';
    this.locale = this.language === 'en' ? 'en-US' : 'it-IT';

    this.groups = this.buildGroups(data?.result || {});
  }

  private buildGroups(result: any): { titleKey: string; rows: AllDataRow[] }[] {
    const operational: AllDataRow[] = [];
    const costs: AllDataRow[] = [];
    const inputs: AllDataRow[] = [];

    for (const [key, raw] of Object.entries(result)) {
      const row = { label: this.label(key), ...this.value(key, raw) };
      // I costi unitari (per infusione, orario infermieri) sono parametri di input, non risultati.
      const isInputCost = /_cost_single_infusion$/.test(key) || key === 'nurses_cost_for_hour';
      if (this.operationalResultKeys.includes(key)) {
        operational.push(row);
      } else if (/cost/i.test(key) && !isInputCost) {
        costs.push(row);
      } else {
        inputs.push(row);
      }
    }

    return [
      { titleKey: 'components.nerf-simulator.operational_metrics', rows: operational },
      { titleKey: 'components.nerf-simulator.cost_parameters', rows: costs },
      { titleKey: 'components.nerf-simulator.input_parameters', rows: inputs },
    ].filter((group) => group.rows.length > 0);
  }

  /**
   * Etichetta nella lingua dell'utente: dal file di lingua (anche tramite alias), altrimenti
   * dalle etichette di riserva del modale, altrimenti il nome della chiave reso leggibile.
   */
  private label(key: string): string {
    const translationKey = `components.nerf-simulator.${this.labelAliases[key] ?? key.toLowerCase()}`;
    const translated = this.translateService.instant(translationKey);
    const label = translated && translated !== translationKey
      ? translated.replace(/\s*\((%|min)\)\s*$/, '')   // l'unità è già nella sua colonna
      : this.fallbackLabels[this.language][key] ?? key.replace(/_/g, ' ').toLowerCase();
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  /** Valore formattato nel locale dell'utente (frazioni mostrate in %) e unità dedotta dal nome della chiave. */
  private value(key: string, raw: any): { value: string; unit: string } {
    if (typeof raw !== 'number') {
      const text = raw == null ? '-' : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw));
      return { value: text, unit: '' };
    }

    const isFraction = /_composition$/.test(key) || key === 'Safety_Buffer' || key === 'No_Show_Rate';
    const value = isFraction ? raw * 100 : raw;
    let unit = '';
    if (isFraction || key === 'utilization_percent') {
      unit = '%';
    } else if (/cost/i.test(key)) {
      unit = /hour/i.test(key) ? 'EUR/h' : 'EUR';
    } else if (key === 'Hours_Per_Day') {
      unit = 'h';
    } else if (key === 'Working_Days_Month') {
      unit = this.dayUnit[this.language];
    } else if (/(^|_)(min|time|gap)(_|$)/i.test(key)) {
      unit = 'min';
    }

    return { value: value.toLocaleString(this.locale, { maximumFractionDigits: 2 }), unit };
  }
}
